import _ from "lodash";
import { AddOrRemoveBasePlanActionTypes } from '@/components/capacity/constants';
import {
  planTypes,
  BASE_PLAN_SUB_TYPES,
  BILLING_CYCLE,
  dependMeetingPlanTypeList,
} from '@/common/constants';
import { checkProMonthlyAndYearlyPrice } from "@/apis/address";
import { getCurrency } from '@/utils/currency';
import { computedPbxBasePlans, computedPbxPlans } from '@/common/service/computers/plans/pbx';
import { filterBundleSeries } from '@/common/product';
import { getPlanTotalLicense } from '@/common/service/validate';
import { flowTypes, pageConfig } from "@/common/service/page-config"
import { $query } from "@/global-install"
import { isZoomMeetingAsAddonCard } from "@/plans/ZOOM_MEETING/common/utils"
import { plansof } from "@/utils/chain"

const state = {
  updateBasePlan: { update: false, from: '' },
  addOrRemoveBasePlanState: { actionType: AddOrRemoveBasePlanActionTypes.NO_ACTION, planType: '' },
  addedTrialToPaidBasePlanList: [],
  syncBaseToSelectedPlanLicense: {currentLicense: 1, baseLicense: 1},
  hasCmrAddonOrAddedCmr: false,
};

const getters = {
  getUsCaBasePlansNumber: (state, getters) => {
    let mixinSelectedPlans = [];

    if (getters.boughtPlans?.length) {
      mixinSelectedPlans = _.reduce(getters.boughtPlans, (pre, x) => {
        const findPlan = _.find(getters.getSelectedPlans, (p) => {
          return (p.plan_code === x.plan_code
            || p.category === x.category
            || (p.type === planTypes.ZOOM_MEETING && x.type === planTypes.ZOOM_MEETING)
            || (p.type === planTypes.ZOOM_ROOMS && x.type === planTypes.ZOOM_ROOMS)
          )
        });

        return [...pre, findPlan ? findPlan : x];
      }, []);
    } else {
      mixinSelectedPlans = getters.getSelectedPlans;
    }

    return mixinSelectedPlans
      .filter(v => [planTypes.ZOOM_MEETING, planTypes.ZOOM_ROOMS].indexOf(v.type) !== -1)
      .reduce((pre, next) => {
        return pre + next.cartInfo.selected_number;
      }, 0);
  },
  currentProMonthlyPlancode: (state, getters) => {
    return getters.planMapFlat.find((item) =>
        item.type === planTypes.ZOOM_MEETING && item.category === BASE_PLAN_SUB_TYPES.PRO && item.billing_cycle === BILLING_CYCLE.MONTHLY
      )?.plan_code || ''
  },
  // PRO INCREASE - get current pro yearly plan code
  currentProYearlyPlancode: (state, getters) => {
    return getters.planMapFlat.find((item) =>
        item.type === planTypes.ZOOM_MEETING && item.category === BASE_PLAN_SUB_TYPES.PRO && item.billing_cycle === BILLING_CYCLE.ANNUAL
      )?.plan_code || ''
  },
  canUpgradeZoneBundle: (state, getters) => {
    if (!getters.basicConfig?.canSelectBizPlus) {
      return false;
    }

    const pbxBoughtPlans = computedPbxPlans(getters.boughtPlans).filter(p => !p.isFreeTrial);
    const pbxBoughtBasePlan = _.get(computedPbxBasePlans(pbxBoughtPlans), [0]);

    return pbxBoughtBasePlan ?  _.get(pbxBoughtBasePlan, 'isOnline', false) : true;
  },
  currentMeetingAndBundlePlans: (state, getters) => {
    return _.filter(getters.getUserCurrentSubscriptions, filterBundleSeries);
  },
  currentMeetingAndBundleLicense: (state, getters) => {
    return getPlanTotalLicense(getters.currentMeetingAndBundlePlans);
  },
  // for zoom meeting/zoom biz plus/zoom united/zoom room
  basePlanTotalLicenses: (state, getters) => {
    const currentMeetingAndBundleLicense = getters.currentMeetingAndBundleLicense;
    const { plans, planType } = getters.getCurrentBasePlanInfo || {};

    if (!currentMeetingAndBundleLicense && _.includes(dependMeetingPlanTypeList, planType)) {
      return getPlanTotalLicense(plans);
    }

    return currentMeetingAndBundleLicense;
  },
  limitLicenseWithBaseZoneTypes: (state, getters) => {
    return _.chain(getters.planMapFlat)
      .filter((p) => _.includes(p.licenseDependBasePlanTypeList, planTypes.ZOOM_MEETING))
      .map(p => p.type)
      .uniq()
      .value();
  },
  limitLicenseWithCurrentBaseTypes: (state, getters) => {
    const basePlanType = getters.getCurrentBasePlanInfo.planType;

    return _.chain(getters.planMapFlat)
      .filter((p) => _.includes(p.licenseDependBasePlanTypeList, basePlanType))
      .map(p => p.type)
      .uniq()
      .value();
  },
  addedTrialToPaidBasePlanList: (state) => state.addedTrialToPaidBasePlanList,
  syncBaseToSelectedPlanLicense: (state) => state.syncBaseToSelectedPlanLicense,
  cmrPaywallTipVisible(state, getters) {
    return !!(
      $query.source === 'recording' &&
      pageConfig.type === flowTypes.basic2pro &&
      !isZoomMeetingAsAddonCard() &&
      // Pro、Biz、Biz Plus、Pro Plus
      plansof(getters.getSelectedPlans)
        .type(planTypes.ZOOM_MEETING, planTypes.ZOOM_ONE_BUNDLE, planTypes.ZONE_PRO_PLUS)
        .has() &&
      // Has CMR addon or has added CMR
      state.hasCmrAddonOrAddedCmr
    )
  },
};

const mutations = {
  setUpdateBasePlan(state, data) {
    state.updateBasePlan = data;
  },
  addOrRemoveBasePlan(state, data) {
    state.addOrRemoveBasePlanState = data;
  },
  setAddedTrialToPaidBasePlanList(state, data) {
    if(!Array.isArray(data)) return;
    state.addedTrialToPaidBasePlanList = data;
  },
  setSelectedPlanLicense(state,data){
    state.syncBaseToSelectedPlanLicense = data
  },
  setHasCmrAddonOrAddedCmr(state, data) {
    state.hasCmrAddonOrAddedCmr = !!data
  },
};

const actions = {
  // PRO INCREASE - update the new increased price plan code for Pro
  checkProMonthlyAndYearlyPriceChange ({ commit, getters }, countryCode) {
    const checkCountryCode = countryCode || getters.addressInfo?.country
    if (!checkCountryCode) return
    checkProMonthlyAndYearlyPrice(checkCountryCode, getCurrency()).then(({result}) => {
      if (!!result.month_plancode && result.month_plancode !== getters.currentProMonthlyPlancode) {
        commit('updateProMonthlyPlanMap', Object.assign({ currentProMonthlyPlancode: getters.currentProMonthlyPlancode, address: checkCountryCode } , result))
      }
      if (!!result.month_plancode && !!result.monthly_amount_overwrite) {
        commit('updateProMonthlyPlanMap', Object.assign({ currentProMonthlyPlancode: getters.currentProMonthlyPlancode, address: checkCountryCode } , result))
      }
      if (!!result.yearly_plancode && result.yearly_plancode !== getters.currentProYearlyPlancode) {
        commit('updateProYearlyPlanMap', Object.assign({ currentProYearlyPlancode: getters.currentProYearlyPlancode, address: checkCountryCode }, result))
      }
    });
  },
  restoreProMonthlyAndYearlyPriceWhenNavBack ({ commit, getters }) {
    if(getters.currentProMonthlyPlancode) {
      commit('restoreProMonthlyPlan', { currentProMonthlyPlancode: getters.currentProMonthlyPlancode })
    }
    if(getters.currentProYearlyPlancode) {
      commit('restoreProYearlyPlan', { currentProYearlyPlancode: getters.currentProYearlyPlancode })
    }
  }
};

export default {
  state,
  getters,
  mutations,
  actions,
};
